import * as React from 'react';
import { Speaker } from 'core/models/catalog/speaker';
import { Paragraph } from '@tikka/client/client-aliases';
import { styled } from 'naan/stitches.config';
import { paletteKey } from '../../../study/views/study-palette';
import { Tooltip } from '@naan/primitives/tooltip';
import { LocationIcon } from '@naan/icons/location-icon';
import { useSwitch2 } from '@naan/hooks/use-switch-2';
import { useOnClickOutside } from '@common/hooks/use-onclick-outside';

const Label = styled('div', {
  $$backgroundColor: paletteKey('$$speakerLabel__backgroundColor_unvisited'),
  $$textColor: paletteKey('$$speakerLabel__color_unvisited'),
  $$borderColor: paletteKey('$$speakerLabel__borderColor_unvisited'),

  backgroundColor: '$$backgroundColor',
  color: '$$textColor',
  border: '1px solid $$borderColor',
  cursor: 'default',

  // display: 'inline-block',
  textStyle: 'body-bold',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  padding: '4px 14px',
  width: 'fit-content',
  borderRadius: '8px',
  flex: 'none',
  order: '0',
  flexGrow: '0',

  marginTop: '$space$6',
  marginBottom: '$space$2',
  transition: 'transform .3s',

  /// @joseph, @frank, there's no support for `visited` labels in the code AFAIK
  '&.visited': {
    $$backgroundColor: paletteKey('$$speakerLabel__backgroundColor_visited'),
    $$textColor: paletteKey('$$speakerLabel__color_visited'),
    $$borderColor: paletteKey('$$speakerLabel__borderColor_visited'),
  },

  '&.current': {
    $$backgroundColor: paletteKey('$$speakerLabel__backgroundColor_current'),
    $$textColor: paletteKey('$$speakerLabel__color_current'),
    $$borderColor: paletteKey('$$speakerLabel__borderColor_current'),
    boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.2)',
  },

  '.showing-translation &': {
    '@playerMedium': {
      transform: 'translateX(-196px)',
    },
  },
});

type Props = {
  label: string;
  speaker: Speaker;
} & React.ComponentProps<typeof Label>;

export const SpeakerLabelPresentation = ({
  label,
  speaker,
  ...props
}: Props) => {
  const tipSwitch = useSwitch2(false);
  const ref = React.useRef<HTMLDivElement>(null);

  useOnClickOutside(ref, () => {
    tipSwitch.off();
  });

  if (!label) {
    return null;
  }

  if (speaker) {
    return (
      <Label {...props} onClick={tipSwitch.toggle} ref={ref}>
        <Tooltip
          tipContent={
            <TooltipContent>
              <span className="bio">{speaker.bio}</span>
              {speaker.accent ? (
                <span className="accent">
                  <LocationIcon />
                  {speaker.accent}
                </span>
              ) : null}
            </TooltipContent>
          }
          showTip={tipSwitch.value}
          placement={'top-start'}
          presentation={'white'}
        >
          <span>{label}</span>
        </Tooltip>
      </Label>
    );
  } else {
    return (
      <Label {...props}>
        <span>{label}</span>
      </Label>
    );
  }
};

const TooltipContent = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  '& > .bio': {
    display: 'block',
    marginBottom: '$space$2',
  },
  '& > .accent': {
    display: 'flex',
    flexDirection: 'row',
    textStyle: 'body',
    color: '$textSecondary',
  },
});
